import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Skeleton from "antd/lib/skeleton";
import useDispatch from "@/Components/Utils/useDispatch";
import { GlobalState } from "@/Reducers/interface";
import {
	AddTaxRegionFormValues,
	AddTaxRegionRequest,
	UserRole,
	PricingPlanNames,
	TaxRegion
} from "@/Contracts/Users";
import UsersActionCreators from "@/Actions/User";
import SettingsActionCreators from "@/Actions/Settings";
import ZMessage from "@molecules/ZMessage";
import { ITaxRegionDrawerProps } from "./interface";
import Form from "antd/lib/form";
import ZForm from "@organisms/ZForm";
import ZDrawer from "@molecules/ZDrawer";
import {
	IFormProps,
	IFormSectionProps,
	ICustomFormItemProps,
	EWidgets
} from "@organisms/ZForm/interface";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import useIsSuperOpsConnected from "@/Hooks/useIsSuperOpsConnected";
import ZAlert from "@atoms/ZAlert";
import ZIcon, { Icons } from "@atoms/ZIcon";
import ZTypography from "@atoms/ZTypography";
import ZCheckbox from "@atoms/ZCheckbox";
import Switch from "antd/lib/switch";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import ZModalConfirm from "@atoms/ZModalConfirmation";
import { FormKeys } from "@/Contracts";

const { ZText } = ZTypography;

const TaxRegionDrawer: React.FC<ITaxRegionDrawerProps> = ({
	visible = false,
	onClose,
	afterSubmit,
	isEditMode = false,
	currentActiveTaxRegion
}) => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();

	const { isLoading, userData, isFormEdited } = useSelector(
		(state: GlobalState) => {
			return {
				isLoading: state.settings.isLoading || state.user.isLoading,
				userData: state.user.userData,
				isFormEdited: state.settings.isFormEdited
			};
		}
	);

	const [defaultRegionDisabled, setDefaultRegionDisabled] = useState<
		boolean | undefined
	>();

	useEffect(() => {
		if (currentActiveTaxRegion) {
			form.setFieldsValue({
				name: currentActiveTaxRegion.name,
				is_default: currentActiveTaxRegion.is_default,
				disabled: !currentActiveTaxRegion?.disabled
			});
		}
	}, [currentActiveTaxRegion]);

	const handleClose = () => {
		form.resetFields();
		onClose && onClose();
		dispatch(
			SettingsActionCreators.resetFormEdited(FormKeys.AddEditTaxRegion)
		);
	};

	const handleCancel = () => {
		if (!isFormEdited?.[FormKeys.AddEditTaxRegion]) {
			handleClose();
		} else {
			ZModalConfirm({
				content: `Are you sure you want to discard ${
					isEditMode ? "editing" : "adding"
				} tax region?`,
				onOk: handleClose
			});
		}
	};

	const addTaxRegion = async (
		addTaxRegionFormValues: AddTaxRegionFormValues
	) => {
		dispatch(SettingsActionCreators.showSettingsLoader());
		try {
			const addTaxRegionRequest: AddTaxRegionRequest = {
				name: addTaxRegionFormValues.name,
				is_default: addTaxRegionFormValues.is_default ? true : false,
				disabled: !addTaxRegionFormValues.disabled
			};
			const { addTaxRegion } = await dispatch(
				UsersActionCreators.addTaxRegion(addTaxRegionRequest)
			);
			await dispatch(UsersActionCreators.getTaxRegionsList());
			handleClose();
			ZMessage.success("Tax Region added successfully");
			if (afterSubmit) {
				afterSubmit(addTaxRegion);
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SettingsActionCreators.hideSettingsLoader());
		}
	};

	const editTaxRegion = async (
		editTaxRegionFormValues: AddTaxRegionFormValues
	) => {
		dispatch(SettingsActionCreators.showSettingsLoader());
		try {
			if (currentActiveTaxRegion) {
				const editTaxRegionRequest: TaxRegion = {
					...currentActiveTaxRegion,
					name: editTaxRegionFormValues.name,
					disabled: !editTaxRegionFormValues.disabled,
					is_default: editTaxRegionFormValues.is_default
				};
				await dispatch(
					UsersActionCreators.editTaxRegion(editTaxRegionRequest)
				);
				await dispatch(UsersActionCreators.getTaxRegionsList());
				handleClose();
				ZMessage.success("Tax Region edited successfully");
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SettingsActionCreators.hideSettingsLoader());
		}
	};

	const hasFeatureAccess = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.AdminRole
	);
	const isSuperOpsConnected = useIsSuperOpsConnected();
	const isFormAllowed = hasFeatureAccess && !isSuperOpsConnected;

	const sections: IFormSectionProps[] = [
		{
			isIndentFormItems: false,
			hideDivider: true,
			meta: {
				formItemLayout: [8, 16],
				fields: [
					{
						key: "name",
						label: "Region Name",
						required: true,
						message: "Please enter region name",
						hasFeedback: true,
						widget: EWidgets.INPUT,
						widgetProps: {
							width: "full",
							placeholder: "Please enter region name"
						},
						rules: [
							{
								whitespace: true,
								message: "Please enter region name"
							}
						]
					},
					{
						key: "is_default",
						label: "Set as default region",
						widget: ({ value, onChange }: ICustomFormItemProps) => {
							const isDefaultDisabled =
								defaultRegionDisabled !== undefined
									? !defaultRegionDisabled
									: isLoading ||
									  currentActiveTaxRegion?.disabled ||
									  currentActiveTaxRegion?.is_default;

							return (
								<>
									<Switch
										size="small"
										checked={value}
										onChange={onChange}
										disabled={isDefaultDisabled}
										className="mt-2"
									/>
									<div
										className={`flex mt-2 items-center ${
											value ? "" : "opacity-0"
										}`}
									>
										<ZIcon
											name={Icons.WARNING}
											size="sm"
											colorVariant={8}
											className="ml-0 mr-2"
										/>
										<ZText colorVariant={8}>
											This will change your existing
											default region to this new region
										</ZText>
									</div>
								</>
							);
						}
					}
				]
			}
		},
		{
			isFooterSection: true,
			meta: {
				fields: [
					{
						key: "disabled",
						initialValue: true,
						widget: ({ value, onChange }: ICustomFormItemProps) => (
							<div className="inline-flex items-center">
								<ZCheckbox
									checked={value}
									onChange={(e: CheckboxChangeEvent) => {
										onChange(e.target.checked);
										setDefaultRegionDisabled(
											e.target.checked
										);
									}}
									disabled={isLoading}
								>
									Active
								</ZCheckbox>
							</div>
						)
					}
				]
			}
		}
	];

	const handleFormChange = () => {
		!isFormEdited?.[FormKeys.AddEditTaxRegion] &&
			dispatch(
				SettingsActionCreators.setFormEdited(FormKeys.AddEditTaxRegion)
			);
	};

	const formProps: IFormProps = {
		key: "tax_region_drawer",
		form,
		loading: isLoading,
		sections,
		onFinish: isEditMode ? editTaxRegion : addTaxRegion,
		onCancel: handleCancel,
		isStickFooter: true,
		submitButtonText: "Save",
		isCancelAllowed: true,
		isRightFooter: true,
		shouldResetFormOnCancel: false,
		onValuesChange: handleFormChange
	};

	return (
		<ZDrawer
			visible={visible}
			closable={true}
			destroyOnClose={true}
			onClose={handleCancel}
			title={`${isEditMode ? "Edit" : "Add"} Tax Region`}
			removeDefaultPadding={true}
		>
			{isSuperOpsConnected ? (
				<ZAlert
					type="info"
					showIcon
					message="Tax configuration is managed by your SuperOps integration and cannot be edited here."
					className="mx-6 mt-4"
				/>
			) : (
				isFormAllowed && (
					<Skeleton
						loading={!userData || userData.id.length === 0}
						active
						paragraph={{ rows: 12 }}
					>
						<ZForm {...formProps} className="py-4" />
					</Skeleton>
				)
			)}
		</ZDrawer>
	);
};

export default TaxRegionDrawer;
